import { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { RemoteParticipant, RemoteTrack, RemoteTrackPublication } from "twilio-video"
import { VideoDisplay } from "@/components/video-display"
import { isAttachableRemoteTrack } from "@/modules/video-call/helpers/track.helper"
import useVideoCallStore from "@/stores/useVideoCallStore"

const ParticipantVideo = () => {
  const { t } = useTranslation()
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null)
  const participants = useVideoCallStore((state) => state.participants)

  const adjustVideoFrame = useCallback(() => {
    const videoElement: HTMLMediaElement | null = remoteVideoRef.current
    if (!videoElement) {
      return
    }

    videoElement.style.width = "100%"
    videoElement.style.height = "100%"
  }, [remoteVideoRef])

  const attachRemoteTrack = useCallback(
    (track: RemoteTrack) => {
      if (isAttachableRemoteTrack(track)) {
        const remoteVideoElement = remoteVideoRef.current

        if (remoteVideoElement && (track.kind === "video" || track.kind === "audio")) {
          track.attach(remoteVideoElement)
          adjustVideoFrame()
        }
      }
    },
    [adjustVideoFrame],
  )

  const subscribeParticipantTracks = useCallback(
    (publication: RemoteTrackPublication) => {
      if (publication.on) {
        publication.on("subscribed", (track) => attachRemoteTrack(track))
      }

      return () => {
        publication.off("subscribed", (track) => attachRemoteTrack(track))
      }
    },
    [attachRemoteTrack],
  )

  const registerParticipantEvents = useCallback(
    (participant: RemoteParticipant) => {
      participant.tracks.forEach((publication) => subscribeParticipantTracks(publication))
      participant.on("trackPublished", (publication) => subscribeParticipantTracks(publication))

      return () => {
        participant.off("trackPublished", (publication) => subscribeParticipantTracks(publication))
      }
    },
    [subscribeParticipantTracks],
  )

  useEffect(() => {
    if (!participants || participants.length === 0) {
      return
    }
    // Twilio room always return list of remote participants
    // In Sofia, we only have one remote participant
    registerParticipantEvents(participants[0])
  }, [participants, registerParticipantEvents])

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-black text-white">
      {participants && participants?.length > 0 ? (
        <VideoDisplay ref={remoteVideoRef} />
      ) : (
        <p>{t("videoCall.waitingForPatientToJoin")}</p>
      )}
    </div>
  )
}

export { ParticipantVideo }
